<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDebounceFn } from '@vueuse/core';
import SegmentsAPI from 'dashboard/api/segments';
import { useAbortableRequest } from 'dashboard/composables/useAbortableRequest';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

// Previa do publico de um segmento de contatos: quantos entram, quantos podem receber, por que os outros ficam de fora e
// uma amostra. Mesma regra do disparo (POST segments/preview). `query` nulo = condicoes incompletas, nada a calcular.
const props = defineProps({
  query: { type: Object, default: null },
});

const emit = defineEmits(['result']);

const PREVIEW_DEBOUNCE_MS = 500;
// Motivos na ordem em que o servidor os atribui, com o texto de cada um.
const EXCLUSION_LABELS = {
  no_phone: 'CONTACTS_LAYOUT.FILTER.SEGMENT_PREVIEW.EXCLUDED.NO_PHONE',
  group: 'CONTACTS_LAYOUT.FILTER.SEGMENT_PREVIEW.EXCLUDED.GROUP',
  blocked: 'CONTACTS_LAYOUT.FILTER.SEGMENT_PREVIEW.EXCLUDED.BLOCKED',
  opted_out: 'CONTACTS_LAYOUT.FILTER.SEGMENT_PREVIEW.EXCLUDED.OPTED_OUT',
  blacklisted: 'CONTACTS_LAYOUT.FILTER.SEGMENT_PREVIEW.EXCLUDED.BLACKLISTED',
};

const { t } = useI18n();
const { run, isPending } = useAbortableRequest();

const preview = ref(null);
const errorMessage = ref('');

const exclusions = computed(() =>
  Object.keys(EXCLUSION_LABELS)
    .map(reason => ({ reason, count: preview.value?.excluded?.[reason] ?? 0 }))
    .filter(({ count }) => count > 0)
);

const fetchPreview = async query => {
  preview.value = null;
  errorMessage.value = '';
  if (!query) {
    emit('result', { valid: false, reachableCount: 0 });
    return;
  }
  try {
    const response = await run(signal =>
      SegmentsAPI.preview(query, { signal })
    );
    if (!response) return;
    preview.value = response.data;
    emit('result', {
      valid: true,
      reachableCount: response.data.reachable_count,
    });
  } catch (error) {
    errorMessage.value =
      error?.response?.data?.error ||
      t('CONTACTS_LAYOUT.FILTER.SEGMENT_PREVIEW.ERROR');
    emit('result', { valid: false, reachableCount: 0 });
  }
};

watch(() => props.query, useDebounceFn(fetchPreview, PREVIEW_DEBOUNCE_MS), {
  deep: true,
  immediate: true,
});
</script>

<template>
  <div
    class="flex flex-col gap-2 p-3 text-sm rounded-lg border border-n-weak bg-n-alpha-1"
  >
    <div class="flex items-center gap-2">
      <span class="font-medium text-n-slate-12">
        {{ t('CONTACTS_LAYOUT.FILTER.SEGMENT_PREVIEW.TITLE') }}
      </span>
      <Spinner v-if="isPending" class="size-4 text-n-slate-10" />
    </div>
    <p v-if="!query" class="text-n-slate-10">
      {{ t('CONTACTS_LAYOUT.FILTER.SEGMENT_PREVIEW.INCOMPLETE') }}
    </p>
    <p v-else-if="errorMessage" class="text-n-ruby-11" data-test="error">
      {{ errorMessage }}
    </p>
    <template v-else-if="preview">
      <p class="text-n-slate-12" data-test="count">
        {{
          t('CONTACTS_LAYOUT.FILTER.SEGMENT_PREVIEW.COUNT', {
            count: preview.count,
          })
        }}
      </p>
      <p
        v-if="preview.reachable_count > 0"
        class="text-n-teal-11"
        data-test="reachable"
      >
        {{
          t('CONTACTS_LAYOUT.FILTER.SEGMENT_PREVIEW.REACHABLE', {
            count: preview.reachable_count,
          })
        }}
      </p>
      <p v-else class="text-n-amber-11" data-test="reachable">
        {{ t('CONTACTS_LAYOUT.FILTER.SEGMENT_PREVIEW.NONE_REACHABLE') }}
      </p>
      <ul v-if="exclusions.length" class="flex flex-wrap gap-x-3 gap-y-1">
        <li
          v-for="{ reason, count } in exclusions"
          :key="reason"
          class="text-n-slate-11"
          :data-test="`excluded-${reason}`"
        >
          {{ t(EXCLUSION_LABELS[reason], { count }) }}
        </li>
      </ul>
      <div v-if="preview.sample.length" class="flex flex-col gap-1">
        <span class="text-xs text-n-slate-10">
          {{ t('CONTACTS_LAYOUT.FILTER.SEGMENT_PREVIEW.SAMPLE') }}
        </span>
        <ul class="flex flex-col gap-0.5" data-test="sample">
          <li
            v-for="contact in preview.sample"
            :key="contact.id"
            class="flex gap-2 text-xs text-n-slate-11"
          >
            <span class="truncate text-n-slate-12">{{ contact.name }}</span>
            <span>{{ contact.phone_number }}</span>
          </li>
        </ul>
      </div>
    </template>
  </div>
</template>
